import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ContactProfileCreateSchema } from '@xsc/shared';
import { api, ApiError } from '../lib/api-client.js';
import { fieldErrors } from '../lib/form-helpers.js';
import { Button } from '../components/ui/button.js';
import { Card, CardBody, CardHeader, CardTitle } from '../components/ui/card.js';
import { Input } from '../components/ui/input.js';
import { FormBanner, FormField } from '../components/ui/form.js';

interface FormState {
  name: string;
  contact_name: string;
  organization_name: string;
  direct_number: string;
  reply_to_email: string;
  timezone: string;
}

const EMPTY: FormState = {
  name: '',
  contact_name: '',
  organization_name: '',
  direct_number: '',
  reply_to_email: '',
  timezone: '',
};

export function NewContactProfilePage(): JSX.Element {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [form, setForm] = useState<FormState>(EMPTY);
  const [errors, setErrors] = useState<Record<string, string> | null>(null);
  const [bannerError, setBannerError] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: (payload: unknown) =>
      api.post('/v1/contact-profiles', payload) as Promise<{ id: string }>,
    onSuccess: (created) => {
      qc.invalidateQueries({ queryKey: ['contact-profiles'] });
      if (created?.id) navigate(`/contact-profiles/${created.id}/edit`);
      else navigate('/contact-profiles');
    },
    onError: (err) => {
      const detail = err instanceof ApiError && typeof err.body === 'object' && err.body && 'detail' in err.body
        ? String((err.body as { detail: unknown }).detail)
        : (err as Error).message;
      setBannerError(detail);
    },
  });

  const onSubmit = (e: FormEvent): void => {
    e.preventDefault();
    setBannerError(null);

    const payload: Record<string, unknown> = {
      name: form.name.trim(),
    };
    if (form.contact_name.trim()) payload['contact_name'] = form.contact_name.trim();
    if (form.organization_name.trim()) payload['organization_name'] = form.organization_name.trim();
    if (form.direct_number.trim()) payload['direct_number'] = form.direct_number.trim();
    if (form.reply_to_email.trim()) payload['reply_to_email'] = form.reply_to_email.trim();
    if (form.timezone.trim()) payload['timezone'] = form.timezone.trim();

    const fieldIssues = fieldErrors(ContactProfileCreateSchema, payload);
    if (fieldIssues) {
      setErrors(fieldIssues);
      return;
    }
    setErrors(null);
    create.mutate(payload);
  };

  const update = (key: keyof FormState) => (value: string): void => {
    setForm((f) => ({ ...f, [key]: value }));
  };

  return (
    <div className="max-w-2xl space-y-4">
      <header>
        <h1 className="text-2xl font-semibold">New contact profile</h1>
        <p className="text-sm text-ink-muted">
          Used as the caller-ID and reply-to identity on events and campaigns.
        </p>
      </header>

      <Card>
        <CardHeader>
          <CardTitle>Profile</CardTitle>
        </CardHeader>
        <CardBody className="space-y-4">
          {bannerError && <FormBanner message={bannerError} />}

          <form onSubmit={onSubmit} className="space-y-4">
            <FormField label="Name" required error={errors?.['name']} hint="Internal label">
              {(id) => (
                <Input
                  id={id}
                  value={form.name}
                  onChange={(e) => update('name')(e.target.value)}
                  required
                  autoFocus
                />
              )}
            </FormField>

            <FormField
              label="Contact name"
              hint="Display name of the contacting party"
              error={errors?.['contact_name']}
            >
              {(id) => (
                <Input id={id} value={form.contact_name} onChange={(e) => update('contact_name')(e.target.value)} />
              )}
            </FormField>

            <FormField
              label="Organization name"
              hint="Used as Caller ID Name"
              error={errors?.['organization_name']}
            >
              {(id) => (
                <Input
                  id={id}
                  value={form.organization_name}
                  onChange={(e) => update('organization_name')(e.target.value)}
                />
              )}
            </FormField>

            <div className="grid grid-cols-2 gap-4">
              <FormField
                label="Caller ID number"
                hint="E.164, e.g. +15555550100"
                error={errors?.['direct_number']}
              >
                {(id) => (
                  <Input
                    id={id}
                    value={form.direct_number}
                    onChange={(e) => update('direct_number')(e.target.value)}
                    placeholder="+15555550100"
                  />
                )}
              </FormField>

              <FormField label="Reply-to email" error={errors?.['reply_to_email']}>
                {(id) => (
                  <Input
                    id={id}
                    type="email"
                    value={form.reply_to_email}
                    onChange={(e) => update('reply_to_email')(e.target.value)}
                  />
                )}
              </FormField>
            </div>

            <FormField
              label="Timezone"
              hint="IANA name, e.g. America/Chicago"
              error={errors?.['timezone']}
            >
              {(id) => (
                <Input
                  id={id}
                  value={form.timezone}
                  onChange={(e) => update('timezone')(e.target.value)}
                  placeholder="America/Chicago"
                />
              )}
            </FormField>

            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="secondary" onClick={() => navigate('/contact-profiles')}>
                Cancel
              </Button>
              <Button type="submit" disabled={create.isPending}>
                {create.isPending ? 'Creating…' : 'Create profile'}
              </Button>
            </div>
          </form>
        </CardBody>
      </Card>
    </div>
  );
}
